/**
 * ApiKeySettings — Manage investor API keys
 *
 * Features:
 *   - List active API keys (name, prefix, last used, revoke)
 *   - Create new keys with name input
 *   - One-time full key reveal in amber warning card
 *   - MCP config snippet (collapsible) with copy
 *   - Empty state when no keys exist
 */

import React, { useState, useCallback, useRef } from 'react';
import { View, Text, TextInput, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import {
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  Clock,
  Shield,
  Terminal,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';
import { hapticLight, hapticSuccess, hapticWarning } from '@/lib/haptics';
import { handleError } from '@/lib/errors';
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
} from '@/hooks/useApiKeys';
import type { ApiKey, CreateApiKeyResponse } from '@/hooks/useApiKeys';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return 'Never';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatDate(iso);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Section header with icon, title, and "New Key" button */
function SectionHeader({
  onNewKey,
  colors,
}: {
  onNewKey: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="flex-row items-center justify-between mb-4">
      <View className="flex-row items-center gap-2.5">
        <View
          className="w-8 h-8 rounded-lg items-center justify-center"
          style={{ backgroundColor: colors.primary[50] }}
        >
          <Key size={16} color={colors.primary[600]} />
        </View>
        <Text
          style={{ color: colors.text.primary }}
          className="text-base font-semibold"
          numberOfLines={1}
        >
          API Keys
        </Text>
      </View>
      <AnimatedPressable
        onPress={() => {
          hapticLight();
          onNewKey();
        }}
        className="flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg"
        style={{ backgroundColor: colors.primary[50] }}
        accessibilityRole="button"
        accessibilityLabel="Create new API key"
      >
        <Plus size={14} color={colors.primary[600]} />
        <Text
          style={{ color: colors.primary[600] }}
          className="text-sm font-medium"
          numberOfLines={1}
        >
          New Key
        </Text>
      </AnimatedPressable>
    </View>
  );
}

/** Create form: name input + Cancel/Create */
function CreateForm({
  onCancel,
  onSubmit,
  isPending,
  colors,
}: {
  onCancel: () => void;
  onSubmit: (name: string) => void;
  isPending: boolean;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const [name, setName] = useState('');
  const inputRef = useRef<TextInput>(null);

  const canSubmit = name.trim().length >= 2 && !isPending;

  return (
    <Animated.View
      entering={FadeInDown.duration(250)}
      className="rounded-xl p-4 mb-3"
      style={{
        backgroundColor: colors.surface.secondary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <Text
        style={{ color: colors.text.primary }}
        className="text-sm font-medium mb-2"
        numberOfLines={1}
      >
        Key Name
      </Text>
      <TextInput
        ref={inputRef}
        value={name}
        onChangeText={setName}
        placeholder="e.g. Claude Desktop, CI Pipeline"
        placeholderTextColor={colors.text.tertiary}
        style={{
          color: colors.text.primary,
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
        className="rounded-lg px-3 py-2.5 text-sm mb-3"
        autoFocus
        maxLength={64}
        returnKeyType="done"
        onSubmitEditing={() => canSubmit && onSubmit(name.trim())}
      />
      <View className="flex-row gap-2 justify-end">
        <AnimatedPressable
          onPress={onCancel}
          disabled={isPending}
          className="px-4 py-2 rounded-lg"
          style={{ backgroundColor: colors.light[300] }}
          accessibilityRole="button"
          accessibilityLabel="Cancel"
        >
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm font-medium"
            numberOfLines={1}
          >
            Cancel
          </Text>
        </AnimatedPressable>
        <AnimatedPressable
          onPress={() => canSubmit && onSubmit(name.trim())}
          disabled={!canSubmit}
          className="px-4 py-2 rounded-lg flex-row items-center gap-1.5"
          style={{
            backgroundColor: canSubmit ? colors.primary[600] : colors.light[300],
          }}
          accessibilityRole="button"
          accessibilityLabel="Create API key"
        >
          {isPending ? (
            <ActivityIndicator size="small" color={colors.text.inverse} />
          ) : (
            <>
              <Key size={14} color={canSubmit ? colors.text.inverse : colors.text.tertiary} />
              <Text
                style={{ color: canSubmit ? colors.text.inverse : colors.text.tertiary }}
                className="text-sm font-semibold"
                numberOfLines={1}
              >
                Create
              </Text>
            </>
          )}
        </AnimatedPressable>
      </View>
    </Animated.View>
  );
}

/** Amber warning card showing full key once */
function CreatedKeyCard({
  fullKey,
  onDismiss,
  colors,
}: {
  fullKey: string;
  onDismiss: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    await Clipboard.setStringAsync(fullKey);
    hapticSuccess();
    setCopied(true);
    if (copyTimeout.current) clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setCopied(false), 2500);
  }, [fullKey]);

  return (
    <Animated.View
      entering={FadeInDown.duration(300)}
      className="rounded-xl p-4 mb-3"
      style={{
        backgroundColor: colors.amber.light,
        borderWidth: 1,
        borderColor: colors.amber.muted,
      }}
    >
      <View className="flex-row items-center gap-2 mb-2">
        <AlertTriangle size={16} color={colors.amber.main} />
        <Text
          style={{ color: colors.amber.main }}
          className="text-sm font-semibold flex-1"
          numberOfLines={1}
        >
          Save your API key now
        </Text>
      </View>
      <Text
        style={{ color: colors.text.secondary }}
        className="text-xs mb-3"
        numberOfLines={2}
      >
        This key will only be shown once. Copy it and store it securely.
      </Text>

      {/* Key display + copy */}
      <View
        className="flex-row items-center rounded-lg px-3 py-2.5 mb-3"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <Text
          style={{ color: colors.text.primary, fontFamily: 'monospace' }}
          className="text-xs flex-1 mr-2"
          numberOfLines={1}
          selectable
        >
          {fullKey}
        </Text>
        <AnimatedPressable
          onPress={handleCopy}
          className="w-8 h-8 rounded-md items-center justify-center"
          style={{ backgroundColor: copied ? colors.traffic.greenLight : colors.light[300] }}
          accessibilityRole="button"
          accessibilityLabel={copied ? 'Copied' : 'Copy API key'}
        >
          {copied ? (
            <Check size={14} color={colors.traffic.green} />
          ) : (
            <Copy size={14} color={colors.text.secondary} />
          )}
        </AnimatedPressable>
      </View>

      <AnimatedPressable
        onPress={() => {
          hapticLight();
          onDismiss();
        }}
        className="flex-row items-center justify-center gap-1.5 py-2 rounded-lg"
        style={{ backgroundColor: colors.amber.muted }}
        accessibilityRole="button"
        accessibilityLabel="Dismiss key warning"
      >
        <Shield size={14} color={colors.amber.main} />
        <Text
          style={{ color: colors.amber.main }}
          className="text-sm font-medium"
          numberOfLines={1}
        >
          I've saved this key
        </Text>
      </AnimatedPressable>
    </Animated.View>
  );
}

/** Individual key card */
function KeyCard({
  apiKey,
  onRevoke,
  colors,
}: {
  apiKey: ApiKey;
  onRevoke: (id: string) => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      className="rounded-xl p-4 mb-2"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <View className="flex-row items-center justify-between">
        <View className="flex-1 mr-3">
          <Text
            style={{ color: colors.text.primary }}
            className="text-sm font-semibold mb-1"
            numberOfLines={1}
          >
            {apiKey.name}
          </Text>
          <View className="flex-row items-center gap-3">
            <Text
              style={{ color: colors.text.tertiary, fontFamily: 'monospace' }}
              className="text-xs"
              numberOfLines={1}
            >
              {apiKey.key_prefix}...
            </Text>
            <View className="flex-row items-center gap-1">
              <Clock size={10} color={colors.text.tertiary} />
              <Text
                style={{ color: colors.text.tertiary }}
                className="text-xs"
                numberOfLines={1}
              >
                {formatRelativeDate(apiKey.last_used_at)}
              </Text>
            </View>
          </View>
        </View>
        <AnimatedPressable
          onPress={() => onRevoke(apiKey.id)}
          className="w-8 h-8 rounded-lg items-center justify-center"
          style={{ backgroundColor: colors.red.light }}
          accessibilityRole="button"
          accessibilityLabel={`Revoke key ${apiKey.name}`}
        >
          <Trash2 size={14} color={colors.red.main} />
        </AnimatedPressable>
      </View>
    </Animated.View>
  );
}

/** Empty state */
function EmptyState({ colors }: { colors: ReturnType<typeof useThemeColors> }) {
  return (
    <View className="items-center py-8">
      <View
        className="w-12 h-12 rounded-full items-center justify-center mb-3"
        style={{ backgroundColor: colors.light[300] }}
      >
        <Key size={20} color={colors.text.tertiary} />
      </View>
      <Text
        style={{ color: colors.text.secondary }}
        className="text-sm font-medium mb-1"
        numberOfLines={1}
      >
        No API keys yet
      </Text>
      <Text
        style={{ color: colors.text.tertiary }}
        className="text-xs text-center px-8"
        numberOfLines={2}
      >
        Create an API key to connect external tools like Claude Desktop via MCP.
      </Text>
    </View>
  );
}

/** Collapsible MCP config snippet */
function McpConfigSection({ colors }: { colors: ReturnType<typeof useThemeColors> }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const configSnippet = JSON.stringify(
    {
      mcpServers: {
        inverio: {
          url: 'https://mcp.inverio.ai/server',
          headers: {
            Authorization: 'Bearer <your-api-key>',
          },
        },
      },
    },
    null,
    2,
  );

  const handleCopy = useCallback(async () => {
    await Clipboard.setStringAsync(configSnippet);
    hapticSuccess();
    setCopied(true);
    if (copyTimeout.current) clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setCopied(false), 2500);
  }, [configSnippet]);

  const ChevronIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <View
      className="rounded-xl mt-3 overflow-hidden"
      style={{
        backgroundColor: colors.surface.secondary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <AnimatedPressable
        onPress={() => {
          hapticLight();
          setExpanded((v) => !v);
        }}
        className="flex-row items-center justify-between px-4 py-3"
        accessibilityRole="button"
        accessibilityLabel={expanded ? 'Collapse MCP config' : 'Expand MCP config'}
      >
        <View className="flex-row items-center gap-2">
          <Terminal size={14} color={colors.text.secondary} />
          <Text
            style={{ color: colors.text.primary }}
            className="text-sm font-medium"
            numberOfLines={1}
          >
            Claude Desktop Config
          </Text>
        </View>
        <ChevronIcon size={16} color={colors.text.tertiary} />
      </AnimatedPressable>

      {expanded && (
        <Animated.View entering={FadeIn.duration(200)} className="px-4 pb-4">
          <Text
            style={{ color: colors.text.tertiary }}
            className="text-xs mb-2"
            numberOfLines={2}
          >
            Add this to your Claude Desktop configuration file:
          </Text>
          <View
            className="rounded-lg p-3"
            style={{
              backgroundColor: colors.dark[900],
            }}
          >
            <Text
              style={{ color: colors.dark[50], fontFamily: 'monospace' }}
              className="text-xs leading-5"
              selectable
            >
              {configSnippet}
            </Text>
          </View>
          <AnimatedPressable
            onPress={handleCopy}
            className="flex-row items-center justify-center gap-1.5 mt-2 py-2 rounded-lg"
            style={{ backgroundColor: colors.light[300] }}
            accessibilityRole="button"
            accessibilityLabel={copied ? 'Copied config' : 'Copy config'}
          >
            {copied ? (
              <>
                <Check size={14} color={colors.traffic.green} />
                <Text
                  style={{ color: colors.traffic.green }}
                  className="text-sm font-medium"
                  numberOfLines={1}
                >
                  Copied
                </Text>
              </>
            ) : (
              <>
                <Copy size={14} color={colors.text.secondary} />
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-sm font-medium"
                  numberOfLines={1}
                >
                  Copy Config
                </Text>
              </>
            )}
          </AnimatedPressable>

          <McpGuideLink colors={colors} />
        </Animated.View>
      )}
    </View>
  );
}

/** Inline link to the full MCP info page (/mcp). */
function McpGuideLink({ colors }: { colors: ReturnType<typeof useThemeColors> }) {
  const router = useRouter();
  return (
    <AnimatedPressable
      onPress={() => {
        hapticLight();
        router.push('/mcp');
      }}
      className="flex-row items-center justify-center gap-1.5 mt-2 py-2"
      accessibilityRole="link"
      accessibilityLabel="Open MCP server guide"
    >
      <Text
        style={{ color: colors.text.brand }}
        className="text-xs font-semibold"
        numberOfLines={1}
      >
        Read the full MCP server guide →
      </Text>
    </AnimatedPressable>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ApiKeySettings() {
  const colors = useThemeColors();
  const { confirmDestructive } = useAlert();

  // Data
  const { data: keys = [], isLoading } = useApiKeys();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  // UI state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleCreate = useCallback(
    async (name: string) => {
      try {
        const result = await createMutation.mutateAsync({ name });
        hapticSuccess();
        setShowCreateForm(false);
        setCreatedKey(result);
      } catch (err) {
        const { isCancelled } = handleError(err, { context: 'ApiKeySettings.handleCreate' });
        if (!isCancelled) hapticWarning();
      }
    },
    [createMutation],
  );

  const handleRevoke = useCallback(
    async (keyId: string) => {
      const key = keys.find((k) => k.id === keyId);
      const confirmed = await confirmDestructive(
        'Revoke API Key',
        `Are you sure you want to revoke "${key?.name ?? 'this key'}"? Any integrations using it will stop working immediately.`,
        'Revoke',
      );
      if (!confirmed) return;

      try {
        await revokeMutation.mutateAsync(keyId);
        hapticSuccess();
      } catch (err) {
        const { isCancelled } = handleError(err, { context: 'ApiKeySettings.handleRevoke' });
        if (!isCancelled) hapticWarning();
      }
    },
    [keys, confirmDestructive, revokeMutation],
  );

  const handleDismissCreatedKey = useCallback(() => {
    setCreatedKey(null);
  }, []);

  const handleOpenCreateForm = useCallback(() => {
    setCreatedKey(null);
    setShowCreateForm(true);
  }, []);

  const handleCancelCreate = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <View className="mt-6">
      <SectionHeader onNewKey={handleOpenCreateForm} colors={colors} />

      {/* Create form */}
      {showCreateForm && (
        <CreateForm
          onCancel={handleCancelCreate}
          onSubmit={handleCreate}
          isPending={createMutation.isPending}
          colors={colors}
        />
      )}

      {/* Created key warning */}
      {createdKey && (
        <CreatedKeyCard
          fullKey={createdKey.key}
          onDismiss={handleDismissCreatedKey}
          colors={colors}
        />
      )}

      {/* Loading */}
      {isLoading && (
        <View className="items-center py-8">
          <ActivityIndicator size="small" color={colors.primary[600]} />
        </View>
      )}

      {/* Key list */}
      {!isLoading && keys.length > 0 && (
        <View>
          {keys.map((key) => (
            <KeyCard
              key={key.id}
              apiKey={key}
              onRevoke={handleRevoke}
              colors={colors}
            />
          ))}
        </View>
      )}

      {/* Empty state */}
      {!isLoading && keys.length === 0 && !showCreateForm && (
        <EmptyState colors={colors} />
      )}

      {/* MCP config */}
      <McpConfigSection colors={colors} />
    </View>
  );
}
